'use client';

import { useState, useTransition } from 'react';
import { useRouter } from 'next/navigation';
import { format } from 'date-fns';
import {
  Archive,
  Check,
  ChevronDown,
  Gift,
  Loader2,
  MoreHorizontal,
  Send,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@kit/ui/badge';
import { Button } from '@kit/ui/button';
import { Checkbox } from '@kit/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@kit/ui/dropdown-menu';
import { toast } from '@kit/ui/sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@kit/ui/table';
import { Trans } from '@kit/ui/trans';

import {
  archiveAuditionOfferAction,
  publishAuditionOffersAction,
} from '../../server/actions/offer-generation.actions';

// ==================== Types ====================
// Match DB CHECK constraints (offers_offer_status_check,
// offers_candidate_status_check). 'for_review' and 'rejected' are NOT
// valid DB values — use 'sent'/'expired'/'withdrawn' for OfferStatus
// and 'declined'/'negotiating' for CandidateStatus.

type OfferStatus =
  | 'draft'
  | 'sent'
  | 'published'
  | 'expired'
  | 'withdrawn'
  | 'archived';
type CandidateStatus = 'pending' | 'accepted' | 'declined' | 'negotiating';

interface OfferWithDancer {
  id: string;
  dancer_id: string;
  application_id: string | null;
  offer_type: string | null;
  offer_status: string | null;
  candidate_status: string | null;
  organization_comment: string | null;
  candidate_comment: string | null;
  start_date: string | null;
  end_date: string | null;
  response_deadline: string | null;
  published_at: string | null;
  responded_at: string | null;
  created_at: string;
  dancer: {
    id: string;
    stage_name: string | null;
    user: {
      first_name: string | null;
      last_name: string | null;
    } | null;
  } | null;
}

interface AuditionOffersListProps {
  auditionId: string;
  offers: OfferWithDancer[];
  total: number;
}

// ==================== Helpers ====================

function getOfferStatusBadgeVariant(
  status: OfferStatus | null,
): 'default' | 'secondary' | 'outline' {
  switch (status) {
    case 'published':
    case 'sent':
      return 'default';
    case 'draft':
      return 'secondary';
    case 'expired':
    case 'withdrawn':
      return 'outline';
    default:
      return 'secondary';
  }
}

function getCandidateStatusBadgeVariant(
  status: CandidateStatus | null,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'accepted':
      return 'default';
    case 'pending':
    case 'negotiating':
      return 'outline';
    case 'declined':
      return 'destructive';
    default:
      return 'secondary';
  }
}

// Returns an i18n key (translated at the call site via t()).
function getOfferStatusLabelKey(status: OfferStatus | null): string {
  switch (status) {
    case 'draft':
      return 'auditions:offersList.offerStatus.draft';
    case 'sent':
      return 'auditions:offersList.offerStatus.sent';
    case 'published':
      return 'auditions:offersList.offerStatus.published';
    case 'expired':
      return 'auditions:offersList.offerStatus.expired';
    case 'withdrawn':
      return 'auditions:offersList.offerStatus.withdrawn';
    case 'archived':
      return 'auditions:offersList.offerStatus.archived';
    default:
      return 'auditions:offersList.offerStatus.unknown';
  }
}

// Returns an i18n key (translated at the call site via t()).
function getCandidateStatusLabelKey(status: CandidateStatus | null): string {
  switch (status) {
    case 'pending':
      return 'auditions:offersList.candidateStatus.pending';
    case 'accepted':
      return 'auditions:offersList.candidateStatus.accepted';
    case 'negotiating':
      return 'auditions:offersList.candidateStatus.negotiating';
    case 'declined':
      return 'auditions:offersList.candidateStatus.declined';
    default:
      return 'auditions:offersList.candidateStatus.unknown';
  }
}

function getDancerDisplayName(
  dancer: OfferWithDancer['dancer'],
  unknownLabel: string,
): string {
  if (!dancer) return unknownLabel;

  if (dancer.stage_name) return dancer.stage_name;

  const firstName = dancer.user?.first_name || '';
  const lastName = dancer.user?.last_name || '';

  if (firstName || lastName) {
    return `${firstName} ${lastName}`.trim();
  }

  return dancer.id.slice(0, 8) + '...';
}

// ==================== Components ====================

export function AuditionOffersList({
  auditionId,
  offers,
  total,
}: AuditionOffersListProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isPublishing, setIsPublishing] = useState(false);

  const draftOffers = offers.filter((o) => o.offer_status === 'draft');
  const hasDraftOffers = draftOffers.length > 0;

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === draftOffers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(draftOffers.map((o) => o.id)));
    }
  };

  const handlePublishSelected = async () => {
    if (selectedIds.size === 0) {
      toast.error(t('auditions:offersList.toast.noneSelected'));
      return;
    }

    setIsPublishing(true);
    try {
      const result = await publishAuditionOffersAction(
        auditionId,
        Array.from(selectedIds),
      );
      if (result.success) {
        toast.success(
          t('auditions:offersList.toast.published', {
            count: result.data.published,
          }),
        );
        setSelectedIds(new Set());
        router.refresh();
      } else {
        toast.error(
          typeof result.error === 'string'
            ? result.error
            : t('auditions:offersList.toast.publishFailed'),
        );
      }
    } catch {
      toast.error(t('auditions:offersList.toast.unexpectedError'));
    } finally {
      setIsPublishing(false);
    }
  };

  const handlePublishAll = async () => {
    setIsPublishing(true);
    try {
      const result = await publishAuditionOffersAction(auditionId);
      if (result.success) {
        toast.success(
          t('auditions:offersList.toast.published', {
            count: result.data.published,
          }),
        );
        router.refresh();
      } else {
        toast.error(
          typeof result.error === 'string'
            ? result.error
            : t('auditions:offersList.toast.publishFailed'),
        );
      }
    } catch {
      toast.error(t('auditions:offersList.toast.unexpectedError'));
    } finally {
      setIsPublishing(false);
    }
  };

  if (offers.length === 0) {
    return (
      <div className="rounded-lg border py-12 text-center">
        <Gift className="text-muted-foreground mx-auto mb-2 h-8 w-8" />
        <h3 className="mb-2 text-lg font-semibold">
          <Trans i18nKey="auditions:offersList.emptyTitle" />
        </h3>
        <p className="text-muted-foreground">
          <Trans i18nKey="auditions:offersList.emptyBody" />
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with bulk actions */}
      <div className="flex items-center justify-between">
        <p className="text-muted-foreground text-sm">
          <Trans
            i18nKey="auditions:offersList.showing"
            values={{ count: offers.length, total }}
          />
        </p>

        {hasDraftOffers && (
          <div className="flex items-center gap-2">
            {selectedIds.size > 0 && (
              <Button
                size="sm"
                onClick={handlePublishSelected}
                disabled={isPublishing}
              >
                {isPublishing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Send className="mr-2 h-4 w-4" />
                )}
                <Trans
                  i18nKey="auditions:offersList.publishSelected"
                  values={{ count: selectedIds.size }}
                />
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <MoreHorizontal className="mr-2 h-4 w-4" />
                  <Trans i18nKey="auditions:offersList.bulkActions" />
                  <ChevronDown className="ml-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={handlePublishAll}
                  disabled={isPublishing}
                >
                  <Send className="mr-2 h-4 w-4" />
                  <Trans
                    i18nKey="auditions:offersList.publishAllDraft"
                    values={{ count: draftOffers.length }}
                  />
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              {hasDraftOffers && (
                <TableHead className="w-[40px]">
                  <Checkbox
                    checked={
                      selectedIds.size === draftOffers.length &&
                      draftOffers.length > 0
                    }
                    onCheckedChange={toggleSelectAll}
                    aria-label={t('auditions:offersList.selectAllAria')}
                  />
                </TableHead>
              )}
              <TableHead>
                <Trans i18nKey="auditions:offersList.colDancer" />
              </TableHead>
              <TableHead>
                <Trans i18nKey="auditions:offersList.colOfferType" />
              </TableHead>
              <TableHead>
                <Trans i18nKey="auditions:offersList.colStatus" />
              </TableHead>
              <TableHead>
                <Trans i18nKey="auditions:offersList.colResponse" />
              </TableHead>
              <TableHead>
                <Trans i18nKey="auditions:offersList.colDeadline" />
              </TableHead>
              <TableHead>
                <Trans i18nKey="auditions:offersList.colCreated" />
              </TableHead>
              <TableHead className="w-[50px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {offers.map((offer) => (
              <OfferRow
                key={offer.id}
                offer={offer}
                auditionId={auditionId}
                showCheckbox={hasDraftOffers}
                isSelected={selectedIds.has(offer.id)}
                onToggleSelect={() => toggleSelection(offer.id)}
                onAction={() => router.refresh()}
              />
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ==================== Row Component ====================

interface OfferRowProps {
  offer: OfferWithDancer;
  auditionId: string;
  showCheckbox: boolean;
  isSelected: boolean;
  onToggleSelect: () => void;
  onAction: () => void;
}

function OfferRow({
  offer,
  auditionId,
  showCheckbox,
  isSelected,
  onToggleSelect,
  onAction,
}: OfferRowProps) {
  const { t } = useTranslation();
  const [isPending, startTransition] = useTransition();
  const offerStatus = offer.offer_status as OfferStatus | null;
  const candidateStatus = offer.candidate_status as CandidateStatus | null;
  const isDraft = offerStatus === 'draft';
  const isPublished = offerStatus === 'published';
  const isArchived = offerStatus === 'archived';

  const handlePublish = () => {
    startTransition(async () => {
      const result = await publishAuditionOffersAction(auditionId, [offer.id]);
      if (result.success) {
        toast.success(t('auditions:offersList.toast.offerPublished'));
        onAction();
      } else {
        toast.error(
          typeof result.error === 'string'
            ? result.error
            : t('auditions:offersList.toast.publishFailed'),
        );
      }
    });
  };

  const handleArchive = () => {
    startTransition(async () => {
      const result = await archiveAuditionOfferAction(offer.id);
      if (result.success) {
        toast.success(t('auditions:offersList.toast.offerArchived'));
        onAction();
      } else {
        toast.error(
          typeof result.error === 'string'
            ? result.error
            : t('auditions:offersList.toast.archiveFailed'),
        );
      }
    });
  };

  const dancerName = getDancerDisplayName(
    offer.dancer,
    t('auditions:offersList.unknownDancer'),
  );

  return (
    <TableRow className={isPending ? 'opacity-50' : ''}>
      {showCheckbox && (
        <TableCell>
          {isDraft && (
            <Checkbox
              checked={isSelected}
              onCheckedChange={onToggleSelect}
              aria-label={t('auditions:offersList.selectOfferAria', {
                name: dancerName,
              })}
            />
          )}
        </TableCell>
      )}
      <TableCell className="font-medium">{dancerName}</TableCell>
      <TableCell>
        <span className="text-muted-foreground text-sm capitalize">
          {offer.offer_type?.replace(/_/g, ' ') ||
            t('auditions:offersList.notSpecified')}
        </span>
      </TableCell>
      <TableCell>
        <Badge variant={getOfferStatusBadgeVariant(offerStatus)}>
          {t(getOfferStatusLabelKey(offerStatus))}
        </Badge>
      </TableCell>
      <TableCell>
        {isPublished ? (
          <Badge variant={getCandidateStatusBadgeVariant(candidateStatus)}>
            {candidateStatus === 'accepted' && (
              <Check className="mr-1 h-3 w-3" />
            )}
            {t(getCandidateStatusLabelKey(candidateStatus))}
          </Badge>
        ) : (
          <span className="text-muted-foreground text-sm">-</span>
        )}
      </TableCell>
      <TableCell className="text-muted-foreground text-sm">
        {offer.response_deadline
          ? format(new Date(offer.response_deadline), 'MMM d, yyyy')
          : '-'}
      </TableCell>
      <TableCell className="text-muted-foreground text-sm">
        {format(new Date(offer.created_at), 'MMM d, yyyy')}
      </TableCell>
      <TableCell>
        {!isArchived && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                disabled={isPending}
              >
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">
                  <Trans i18nKey="auditions:offersList.rowActions" />
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {isDraft && (
                <>
                  <DropdownMenuItem
                    onClick={handlePublish}
                    disabled={isPending}
                  >
                    <Send className="mr-2 h-4 w-4" />
                    <Trans i18nKey="auditions:offersList.publish" />
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem
                onClick={handleArchive}
                disabled={isPending}
                className="text-destructive focus:text-destructive"
              >
                <Archive className="mr-2 h-4 w-4" />
                <Trans i18nKey="auditions:offersList.archive" />
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </TableCell>
    </TableRow>
  );
}
